<template>
  <el-header class="header">
    <div class="header-content">
      <div class="logo-section">
        <img :src="logoImage" :alt="schoolStore.getCurrentSchool?.name || '学校Logo'" class="logo-img" @error="handleLogoError" />
      </div>
      
      <div class="nav-links">
        <el-link type="primary" :underline="false" @click="goToHome">首页</el-link>
        <el-link type="primary" :underline="false" @click="goToAllCourses">全部课程</el-link>
        <el-link type="primary" :underline="false" @click="goToPublicCourses">公开课</el-link>
        <el-link type="primary" :underline="false" @click="goToLiveCourse">云课直播</el-link>
        <el-link type="primary" :underline="false" @click="goToDigitalResource">数图资源</el-link>
      </div>
      
      <div class="search-section">
        <el-input 
          placeholder="请输入搜索课程" 
          prefix-icon="Search" 
          class="search-input" 
        />
      </div>
      
      <div class="user-section">
        <el-link type="primary" :underline="false" @click="goToPersonalCenter">个人中心</el-link>
        <el-avatar :size="32" :src="avatarImage" />
        <span class="username">{{ studentInfo.name || '加载中' }}</span>
        <el-link type="primary" :underline="false" @click="logout">
          <img :src="exitIcon" alt="退出" class="exit-icon" />
          退出
        </el-link>
      </div>
    </div>
  </el-header>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { removeToken } from '@/utils/auth'
import { useUserStore } from '@/stores/user'
import { useSchoolStore } from '@/stores/school'
import { studentLogout } from '@/api/login'

// 获取路由实例
const router = useRouter()
const userStore = useUserStore()

// 用户信息响应式变量
const studentInfo = ref({})

// 跳转到首页
const goToHome = () => {
  router.push('/student/home')
}

// 跳转到全部课程页面
const goToAllCourses = () => {
  // 跳转到首页的全部课程页面
  router.push('/mo_course')
}

// 跳转到公开课页面
const goToPublicCourses = () => {
  // 跳转到首页的公开课页面
  router.push('/course')
}

// 跳转到云课直播页面
const goToLiveCourse = () => {
  // 跳转到首页的云课直播页面
  router.push('/live')
}

// 跳转到数图资源外部网站
const goToDigitalResource = () => {
  // 在新窗口中打开外部网站
  window.open('https://szzy.yinghuaonline.com/', '_blank')
}

// 修改个人中心跳转，使其与路由配置一致
const goToPersonalCenter = () => {
  router.push('/student/personal-settings')
}

// 从localStorage获取用户信息的函数
const loadUserInfo = () => {
  try {
    // 从localStorage获取用户信息
    const savedUserInfo = localStorage.getItem('userInfo')
    if (savedUserInfo) {
      const parsedInfo = JSON.parse(savedUserInfo)
      if (parsedInfo && typeof parsedInfo === 'object') {
        studentInfo.value = parsedInfo
        console.log('StudentHeader更新用户信息:', parsedInfo)
      }
    }
  } catch (error) {
    console.error('获取用户信息失败:', error)
  }
}

// 处理自定义事件 - 用户信息更新
const handleUserInfoUpdated = () => {
  loadUserInfo()
}

// 处理路由变化，优化更新时机
const handleRouteChange = (to) => {
  // 当路由跳转到首页或个人中心时更新用户信息
  if (to.path === '/student/home' || to.path === '/student/personal-settings') {
    loadUserInfo()
  }
}

onMounted(() => {
  // 初始加载时获取一次用户信息
  loadUserInfo()
  
  // 主动调用fetchUserInfo确保获取最新数据
  if (!studentInfo.value.name) {
    userStore.fetchUserInfo().then(() => {
      loadUserInfo() // 重新加载localStorage中的数据
    })
  }
  
  // 添加localStorage变化监听（跨标签页同步）
  window.addEventListener('storage', handleStorageChange)
  
  // 添加自定义事件监听（页面内部数据更新通知）
  window.addEventListener('userInfoUpdated', handleUserInfoUpdated)
  
  // 监听路由变化，只在跳转到主页时更新
  const routeWatcher = router.afterEach(handleRouteChange)
  
  // 组件卸载时清除事件监听
  onUnmounted(() => {
    window.removeEventListener('storage', handleStorageChange)
    window.removeEventListener('userInfoUpdated', handleUserInfoUpdated)
    routeWatcher() // 移除路由监听器
  })
})

// 处理localStorage变化
const handleStorageChange = (event) => {
  if (event.key === 'userInfo') {
    loadUserInfo()
  }
}

// 退出登录
const logout = async () => {
  try {
    await ElMessageBox.confirm('确定要退出登录吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    try {
      await studentLogout()
    } catch (apiError) {
      console.warn('退出登录API调用失败，继续本地清理:', apiError)
    }
    
    try {
      userStore.logout()
    } finally {
      removeToken('student')
      localStorage.removeItem('student_token')
      localStorage.removeItem('travel_token')
      localStorage.removeItem('currentSchool')
      sessionStorage.clear()
    }
    
    ElMessage.success('退出登录成功')
    router.replace({ path: '/student/login', query: { reason: 'logout', _t: String(Date.now()) } })
  } catch {
    // 用户取消退出
  }
}

// 默认头像路径
const defaultAvatar = require('@/assets/student_assets/assets/img-touxiang@2x.png')

// 头像图片计算属性 - 优先使用studentInfo中的头像URL
const avatarImage = computed(() => {
  // 尝试从studentInfo中获取头像URL，可以处理多种可能的字段名
  const userAvatar = studentInfo.value?.avatar || 
                     studentInfo.value?.avatarUrl || 
                     studentInfo.value?.headImg
  // 如果存在用户头像URL则使用，否则使用默认头像
  return userAvatar || defaultAvatar
})

// 创建学校store实例
const schoolStore = useSchoolStore()

// 规范化logo URL函数
const normalizeLogoUrl = (logo) => {
  if (!logo) return ''
  const raw = String(logo).trim()
  if (!raw) return ''
  if (/^(https?:)?\/\//i.test(raw)) return raw  // 完整URL直接返回
  if (/^(data:|blob:)/i.test(raw)) return raw   // 数据URL直接返回
  if (raw.startsWith('/api/')) return raw        // 以/api/开头直接返回
  if (raw.startsWith('/')) return `/api${raw}`   // 以/开头添加/api前缀
  return `/api/${raw}`                           // 其他情况添加/api/前缀
}

// 处理logo加载错误
const handleLogoError = (event) => {
  event.target.src = defaultLogo
}

// 默认logo路径
const defaultLogo = require('@/assets/student_assets/assets/logo@2x.png')

// logo图片计算属性 - 优先使用schoolStore中的学校logo
const logoImage = computed(() => {
  // 从schoolStore获取当前学校信息
  const currentSchool = schoolStore.getCurrentSchool
  const logo = normalizeLogoUrl(currentSchool?.logo)
  return logo || defaultLogo
})

// 其他图片路径
const exitIcon = ref(require('@/assets/student_assets/assets/icon-tuichu@2x.png'))
</script>

<style scoped>
/* 头部样式 */
.header {
  background: #fff;
  border-bottom: 1px solid #e4e7ed;
  padding: 0;
  height: 60px;
  line-height: 60px;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.logo-section {
  display: flex;
  align-items: center;
}

.logo-img {
  width: 250px;
  height: 60px;
  object-fit: contain;
  margin-right: 10px;
}

.nav-links {
  display: flex;
  gap: 30px;
}

.search-input {
  width: 150px;
}

.user-section {
  display: flex;
  align-items: center;
  gap: 15px;
}

.username {
  color: #333;
  font-weight: 500;
}

.exit-icon {
  width: 16px;
  height: 16px;
  margin-right: 5px;
  vertical-align: middle;
}

/* 覆盖Element UI默认样式 */
a.el-link.el-link--primary {
  color: black !important;
}

.el-link.el-link--primary {
  --el-link-text-color: #000000 !important;
  --el-link-hover-text-color: var(--el-color-primary-light-3);
  --el-link-disabled-text-color: var(--el-color-primary-light-5);
}
</style>
